/**
 * Which coop-relative paths the Read feature cares about, and how to bucket a
 * delta entry. Pure — shared by the sync store and its tests.
 */
import { monthFromCluckPath } from '../shared/clucks.js';
import type { StorageEntry } from '../shared/api-types.js';

/** A browsable nest page: `nest/{entities,concepts,sources}/<slug>.md`. */
export function isNestPage(path: string): boolean {
  if (!path.startsWith('nest/') || !path.endsWith('.md')) return false;
  if (path === 'nest/index.md') return false;
  const dir = path.split('/')[1];
  return dir === 'entities' || dir === 'concepts' || dir === 'sources';
}

/** A skill-generated file in `exports/` (a deck, doc, chart…). */
export function isExport(path: string): boolean {
  return /^exports\/[^/]+/.test(path);
}

export type EntryBucket =
  | { kind: 'page'; op: 'upsert' | 'delete'; path: string }
  | { kind: 'cluck'; op: 'upsert' | 'delete'; path: string; month: string }
  | { kind: 'export'; op: 'upsert' | 'delete'; path: string }
  | { kind: 'ignore' };

export function classifyEntry(e: StorageEntry): EntryBucket {
  const month = monthFromCluckPath(e.path);
  if (month) {
    return { kind: 'cluck', op: e.tag === 'deleted' ? 'delete' : 'upsert', path: e.path, month };
  }
  if (isNestPage(e.path)) {
    return { kind: 'page', op: e.tag === 'deleted' ? 'delete' : 'upsert', path: e.path };
  }
  if (isExport(e.path)) {
    return { kind: 'export', op: e.tag === 'deleted' ? 'delete' : 'upsert', path: e.path };
  }
  return { kind: 'ignore' };
}
